// src/components/GoogleSignInButton.js - Shared Google Sign In component
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const GoogleSignInButton = ({ buttonText = "continue_with", onSuccess, onError }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleSignIn,
            auto_select: false,
            cancel_on_tap_outside: true,
            use_fedcm_for_prompt: true
          });
          
          // Render the Google button
          window.google.accounts.id.renderButton(
            document.getElementById("googleSignInButton"),
            {
              type: "standard",
              theme: "outline", 
              size: "large",
              text: buttonText, // "signin_with" or "signup_with" or "continue_with"
              shape: "rectangular",
              logo_alignment: "left",
              width: 280
            }
          );
        } catch (error) {
          console.error('Failed to initialize Google Sign-In:', error);
          if (onError) onError('Failed to initialize Google Sign-In');
        }
      }
    };

    // Wait for Google script to load
    if (window.google && window.google.accounts) {
      initializeGoogleSignIn();
    } else {
      // If script hasn't loaded yet, wait for it
      const intervalId = setInterval(() => {
        if (window.google && window.google.accounts) {
          initializeGoogleSignIn();
          clearInterval(intervalId);
        }
      }, 100);
      
      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [buttonText]);

  const handleGoogleSignIn = async (response) => {
    try {
      setLoading(true);
      
      // Send Google credential to our backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const res = await fetch(`${apiUrl}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: response.credential
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        // Pass structured error for access-gated responses
        if (data.code === 'ACCOUNT_PENDING' || data.code === 'ACCOUNT_SUSPENDED') {
          if (onError) {
            onError(data.message, data);
          }
          return;
        }
        throw new Error(data.message || 'Google authentication failed');
      }

      // Store token and user info
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Call success callback if provided, otherwise navigate
      if (onSuccess) {
        onSuccess(data);
      } else {
        const returnTo = location.state?.returnTo || '/';
        navigate(returnTo);
      }
    } catch (error) {
      console.error('Google authentication error:', error);
      const errorMessage = error.message || 'Google authentication failed. Please try again.';

      if (onError) {
        onError(errorMessage);
      } else {
        console.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center',
      marginBottom: '20px',
      opacity: loading ? 0.6 : 1,
      pointerEvents: loading ? 'none' : 'auto'
    }}>
      <div id="googleSignInButton"></div>
    </div>
  );
};

export default GoogleSignInButton;